import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Bot, Maximize2, SquarePen, X } from 'lucide-react'
import { assistentBereichFuerPfad } from '@symphenia/shared-types'
import { assistentApi, type AssistentKontext } from '@/lib/assistent/assistent.api'
import { useAssistent, useDarfAssistent } from '@/lib/assistent/assistent.store'
import { onNavEnter, onNavLeave } from '@/components/layout/navigation'
import { Gespraech } from './Gespraech'

const BREITE_SCHLUESSEL = 'assistent-breite'
const BREITE_VORGABE = 420
const BREITE_MIN = 320
const BREITE_MAX = 900
/** Unter dem Menüpunkt „Assistent" ist das Gespräch die Seite selbst — dort gibt es keine Leiste daneben. */
const SEITE = '/assistant'
/**
 * Datensätze, die **ihr** Gespräch haben (7.5): Wer die Leiste in einer Kalkulation öffnet, sieht das
 * jüngste Gespräch dieser Kalkulation weiter — wie in ihrer früheren Leiste. Überall sonst bleibt das
 * Gespräch, bis der Benutzer ein neues beginnt.
 */
const EIGENES_GESPRAECH = ['calculation']

const liesBreite = () => {
  try {
    const w = Number(localStorage.getItem(BREITE_SCHLUESSEL))
    return Number.isFinite(w) && w >= BREITE_MIN && w <= BREITE_MAX ? w : BREITE_VORGABE
  } catch {
    return BREITE_VORGABE
  }
}

const aufSeite = (pathname: string) => pathname === SEITE || pathname.startsWith(`${SEITE}/`)

/**
 * AP-18 5.1 — Tastenkürzel **Strg + K** (⌘ + K) öffnet und schließt die Seitenleiste, von überall.
 * Einmal im Rahmen der Anwendung.
 */
export function useAssistentTaste() {
  const darf = useDarfAssistent()
  const umschalten = useAssistent((s) => s.umschalten)
  const { pathname } = useLocation()
  const aktiv = darf && !aufSeite(pathname)
  useEffect(() => {
    if (!aktiv) return
    const taste = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        // Der Browser legt auf Strg + K seine Adresszeile — in der Anwendung gehört die Taste dem Assistenten.
        e.preventDefault()
        umschalten()
      }
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [aktiv, umschalten])
}

/** Symbol „Assistent" im Menükopf, neben dem Posteingang. */
export function AssistentSymbol() {
  const { t } = useTranslation()
  const darf = useDarfAssistent()
  const offen = useAssistent((s) => s.offen)
  const umschalten = useAssistent((s) => s.umschalten)
  const { pathname } = useLocation()
  if (!darf || aufSeite(pathname)) return null
  return (
    <button
      type="button"
      onClick={umschalten}
      className="rounded-md p-1.5 transition-colors"
      style={{ color: 'var(--sidebar-fg)' }}
      onMouseEnter={onNavEnter}
      onMouseLeave={onNavLeave}
      title={t('assistant.open')}
      aria-label={t('assistant.title')}
      aria-pressed={offen}
    >
      <Bot size={18} />
    </button>
  )
}

/**
 * AP-18 5.1 — **Seitenleiste des Assistenten**, von jeder Seite aus. Auf breiten Schirmen steht sie
 * neben der Seite und nimmt ihr Platz weg, statt sie zu verdecken; auf dem Telefon ist sie ein
 * Blatt über den ganzen Schirm. Die Breite merkt sich der Browser.
 *
 * Mit jeder Frage geht mit, wo der Benutzer ist: Bereich und Titel der Seite (aus dem Menü) und —
 * wenn die Seite ihn gemeldet hat (`useAssistentDatensatz`) — der geöffnete Datensatz.
 */
export function AssistentLeiste({ seitenTitel }: { seitenTitel: string }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const darf = useDarfAssistent()
  const offen = useAssistent((s) => s.offen)
  const gespraechId = useAssistent((s) => s.gespraechId)
  const vorgabe = useAssistent((s) => s.vorgabe)
  const datensatz = useAssistent((s) => s.datensatz)
  const schliessen = useAssistent((s) => s.schliessen)
  const setGespraech = useAssistent((s) => s.setGespraech)
  const vorgabeVerbraucht = useAssistent((s) => s.vorgabeVerbraucht)
  const [breite, setBreite] = useState(liesBreite)

  // Wer zum Menüpunkt „Assistent" wechselt, hat das Gespräch dort — die Leiste ist damit zu und
  // steht nicht wieder da, sobald er die Seite verlässt.
  const dort = aufSeite(pathname)
  useEffect(() => {
    if (dort && offen) schliessen()
  }, [dort, offen, schliessen])

  // In einer Kalkulation: ihr jüngstes Gespräch — einmal je Kalkulation, damit „Neues Gespräch" eines bleibt.
  // Der Server holt dabei, falls es ihn gibt, den Verlauf der früheren Leiste herüber.
  const gebunden = datensatz && EIGENES_GESPRAECH.includes(datensatz.art) ? `${datensatz.art}:${datensatz.id}` : null
  const gewaehltFuer = useRef<string | null>(null)
  useEffect(() => {
    if (!darf || !offen || !gebunden || gewaehltFuer.current === gebunden) return
    const [art, id] = [gebunden.slice(0, gebunden.indexOf(':')), gebunden.slice(gebunden.indexOf(':') + 1)]
    let weg = false
    void assistentApi
      .liste({ contextKind: art, contextId: id, limit: 1 })
      .then((l) => {
        if (weg) return
        gewaehltFuer.current = gebunden
        setGespraech(l.data[0]?.id ?? null)
      })
      .catch(() => undefined)
    return () => {
      weg = true
    }
  }, [darf, offen, gebunden, setGespraech])

  const kontext = useMemo<AssistentKontext>(
    () => ({
      bereich: assistentBereichFuerPfad(pathname),
      seite: pathname,
      ...(seitenTitel ? { seitenTitel } : {}),
      ...(datensatz ? { art: datensatz.art, id: datensatz.id, bezeichnung: datensatz.bezeichnung, ...(datensatz.auswahl?.length ? { auswahl: datensatz.auswahl } : {}) } : {}),
    }),
    [pathname, seitenTitel, datensatz],
  )

  if (!darf || !offen || aufSeite(pathname)) return null

  function ziehen(e: React.MouseEvent) {
    e.preventDefault()
    const start = e.clientX
    const alt = breite
    let jetzt = alt
    const bewege = (m: MouseEvent) => {
      jetzt = Math.min(BREITE_MAX, Math.max(BREITE_MIN, alt + start - m.clientX))
      setBreite(jetzt)
    }
    const los = () => {
      document.removeEventListener('mousemove', bewege)
      document.removeEventListener('mouseup', los)
      try {
        localStorage.setItem(BREITE_SCHLUESSEL, String(jetzt))
      } catch {
        // Browser ohne Speicher — dann gilt die Vorgabe beim nächsten Mal
      }
    }
    document.addEventListener('mousemove', bewege)
    document.addEventListener('mouseup', los)
  }

  const ort = [seitenTitel, datensatz?.bezeichnung].filter(Boolean).join(' · ')
  const knopf = 'rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground'
  return (
    <aside
      className="fixed inset-0 z-40 flex flex-col border-l border-border lg:relative lg:inset-auto lg:z-auto lg:w-[var(--assistent-breite)] lg:max-w-[60vw] lg:flex-shrink-0"
      style={{ '--assistent-breite': `${breite}px`, background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' } as CSSProperties}
      aria-label={t('assistant.title')}
    >
      <div className="absolute bottom-0 left-0 top-0 hidden w-1.5 cursor-col-resize hover:bg-primary/40 lg:block" onMouseDown={ziehen} />
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-medium">
            <Bot className="h-5 w-5 text-primary" /> {t('assistant.title')}
          </div>
          <div className="flex items-center gap-1">
            {gespraechId && (
              <button type="button" className={knopf} onClick={() => setGespraech(null)} title={t('assistant.newConversation')} aria-label={t('assistant.newConversation')}>
                <SquarePen className="h-4 w-4" />
              </button>
            )}
            <Link to={gespraechId ? `${SEITE}/${gespraechId}` : SEITE} className={knopf} title={t('assistant.openInAssistant')} aria-label={t('assistant.openInAssistant')}>
              <Maximize2 className="h-4 w-4" />
            </Link>
            <button type="button" className={knopf} onClick={schliessen} aria-label={t('assistant.close')} title={t('assistant.close')}>
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {ort && <p className="mt-1 truncate text-xs text-muted-foreground" title={ort}>{t('assistant.youAreIn', { ort })}</p>}
      </div>
      <Gespraech gespraechId={gespraechId} onGespraech={setGespraech} kontext={kontext} vorgabe={vorgabe} onVorgabeVerbraucht={vorgabeVerbraucht} />
    </aside>
  )
}
